import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../contexts/UserContext';
import {
  Typography, Box, Paper, Grid, Chip, Button,
  LinearProgress, Divider, CircularProgress
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import QuizIcon from '@mui/icons-material/Quiz';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { content } from '../api/client';

const MODULE_COLORS = {
  code_intro: '#f44336',
  r_eco: '#9c27b0',
  r_bio: '#2196f3',
  r_cell: '#ff9800',
  py_worm: '#4caf50',
  resources: '#ffca28'
};

export default function ProgressTracker() {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    content.modules()
      .then(({ data }) => setModules(data))
      .finally(() => setLoading(false));
  }, []);

  const getQuizResult = (moduleId, courseId) => {
    const key = `quiz_${moduleId}_${courseId}`;
    return JSON.parse(localStorage.getItem(key) || 'null');
  };

  if (loading) return <CircularProgress sx={{ m: 4 }} />;

  const learningModules = modules.filter(m => m.id !== 'resources');

  return (
    <Box sx={{ mt: 2, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Your Progress
      </Typography>
      {!user && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#fff3e0' }}>
          <Typography>
            Log in to save your progress to the server. Your quiz results are currently saved locally in your browser.
          </Typography>
        </Paper>
      )}

      <Grid container spacing={3}>
        {learningModules.map(mod => {
          const courses = mod.courses || [];
          const passedCount = courses.filter(c => getQuizResult(mod.id, c.id)?.passed).length;
          const totalCourses = courses.length;
          const completionPct = totalCourses > 0 ? Math.round((passedCount / totalCourses) * 100) : 0;
          const allDone = passedCount === totalCourses && totalCourses > 0;
          const color = MODULE_COLORS[mod.id] || '#607d8b';

          return (
            <Grid item xs={12} key={mod.id}>
              <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                <Box sx={{ bgcolor: color, px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {mod.icon && <img src={mod.icon} alt="" style={{ height: 36, width: 36, objectFit: 'contain' }} />}
                    <Typography variant="h6" color="white" fontWeight="bold">{mod.title}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {allDone && <EmojiEventsIcon sx={{ color: '#FFD700', fontSize: 28 }} />}
                    <Chip
                      label={`${passedCount}/${totalCourses} courses`}
                      sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                      size="small"
                    />
                  </Box>
                </Box>

                <Box sx={{ px: 3, pt: 2, pb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                    <LinearProgress
                      variant="determinate"
                      value={completionPct}
                      sx={{ flex: 1, height: 8, borderRadius: 4, bgcolor: '#e0e0e0', '& .MuiLinearProgress-bar': { bgcolor: color } }}
                    />
                    <Typography variant="body2" fontWeight="bold" minWidth={40}>{completionPct}%</Typography>
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  <Grid container spacing={1.5}>
                    {courses.map(course => {
                      const result = getQuizResult(mod.id, course.id);
                      return (
                        <Grid item xs={12} sm={6} key={course.id}>
                          <Box sx={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            p: 1.5, borderRadius: 2, border: '1px solid',
                            borderColor: result?.passed ? 'success.200' : 'grey.200',
                            bgcolor: result?.passed ? '#f0fdf4' : 'grey.50'
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {result?.passed
                                ? <CheckCircleIcon color="success" fontSize="small" />
                                : <QuizIcon color={result ? 'warning' : 'disabled'} fontSize="small" />
                              }
                              <Typography variant="body2" fontWeight="bold" noWrap maxWidth={160}>
                                {course.title}
                              </Typography>
                            </Box>
                            {result ? (
                              <Chip
                                label={`${result.score}%`}
                                size="small"
                                color={result.passed ? 'success' : 'warning'}
                                variant={result.passed ? 'filled' : 'outlined'}
                              />
                            ) : (
                              <Chip label="Not started" size="small" variant="outlined" color="default" />
                            )}
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>

                  <Box sx={{ display: 'flex', gap: 1, mt: 2, mb: 1, justifyContent: 'flex-end' }}>
                    <Button size="small" variant="outlined" onClick={() => navigate(`/module/${mod.id}`)}>
                      Go to Module
                    </Button>
                    {allDone && (
                      <Button size="small" variant="contained" color="success" onClick={() => navigate(`/module/${mod.id}/complete`)}>
                        View Completion
                      </Button>
                    )}
                  </Box>
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
